import React from "react";
import { Box, Divider, Typography } from "@mui/material";
import { ParentSectionType } from "../../models/Event";
import DialogStore from "../../stores/DialogStore";
import { capitalizeWords, priceFormatter } from "../../utils/helpers";

/**
 * The confirmation shown when a pick (or a removal) would leave a seat with
 * nobody able to sit beside it. Shared by the seatmap and the cart so both
 * read the same, and routed through DialogStore because the cart has no
 * seatmap of its own to hang a dialog off.
 */

/** One seat line in the prompt, resolved by the caller before opening. */
export type StrandedSeatEntry = {
    id: number;
    /** e.g. "Section A · Row 3 · Seat 12". */
    label: string;
    /** Ticket type / price group name, when the seat resolved to one. */
    typeName?: string;
    /** In cents; null when the seat has no resolvable price. */
    price: number | null;
    color: string;
};

export const PACKAGE_SEAT_COLOR = "#009000";

export function strandedSeatLabel(seat: {
    name: string;
    parentSections: ParentSectionType[];
}): string {
    return [
        ...seat.parentSections
            .filter((ps) => ps.name)
            .map((ps) => `${capitalizeWords(ps.type)} ${ps.name}`),
        `Seat ${seat.name}`,
    ].join(" · ");
}

const SeatCard = ({
    entry,
    caption,
}: {
    entry: StrandedSeatEntry;
    caption: string;
}) => (
    <Box
        sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
            my: 1,
            p: 2,
            borderRadius: 2,
            backgroundColor: `${entry.color}20`,
            border: `1px solid ${entry.color}90`,
        }}
    >
        <Box>
            <Typography
                variant="caption"
                sx={{ color: entry.color, fontWeight: 600 }}
            >
                {caption}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
                {entry.label}
            </Typography>
            {entry.typeName && (
                <Typography variant="caption" color="text.secondary">
                    {entry.typeName}
                </Typography>
            )}
        </Box>
        {entry.price != null && (
            <Typography variant="body2" fontWeight={600}>
                {priceFormatter(entry.price)}
            </Typography>
        )}
    </Box>
);

const SeatList = ({
    mode,
    picked,
    companions,
}: {
    mode: "add" | "remove";
    picked: StrandedSeatEntry;
    companions: StrandedSeatEntry[];
}) => {
    const prices = [picked, ...companions].map((e) => e.price);
    const totalPrice = prices.every((p) => p != null)
        ? (prices as number[]).reduce((sum, p) => sum + p, 0)
        : null;

    return (
        <Box>
            <SeatCard
                entry={picked}
                caption={mode === "add" ? "Your seat" : "Removing"}
            />
            {companions.map((entry) => (
                <SeatCard
                    key={entry.id}
                    entry={entry}
                    caption={mode === "add" ? "Also required" : "Also removed"}
                />
            ))}
            {mode === "add" && totalPrice != null && (
                <>
                    <Divider sx={{ my: 1 }} />
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            px: 2,
                        }}
                    >
                        <Typography variant="body2" fontWeight={600}>
                            Total for {companions.length + 1} seats
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                            {priceFormatter(totalPrice)}
                        </Typography>
                    </Box>
                </>
            )}
        </Box>
    );
};

export function promptStrandedSeats(args: {
    mode: "add" | "remove";
    picked: StrandedSeatEntry;
    companions: StrandedSeatEntry[];
    /** How many seats would be orphaned; defaults to the companion count. */
    strandedCount?: number;
    onConfirm: () => void;
}) {
    const { mode, picked, companions, strandedCount, onConfirm } = args;
    const total = companions.length + 1;
    const lonely =
        (strandedCount ?? companions.length) === 1
            ? "a single seat"
            : "single seats";

    DialogStore.showAlert(
        mode === "add"
            ? "These seats have to be bought together"
            : "These seats have to be released together",
        mode === "add"
            ? `Taking ${picked.label} on its own would leave ${lonely} that nobody else can buy.`
            : `Giving up ${picked.label} on its own would leave ${lonely} that nobody else can buy.`,
        onConfirm,
        mode === "add" ? `Add ${total} Seats` : `Remove ${total} Seats`,
        mode === "add" ? "Cancel" : "Keep My Seats",
        () => (
            <SeatList mode={mode} picked={picked} companions={companions} />
        ),
    );
}
